import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../../lib/api";
import { canEditProtectedTaskFields } from "../../lib/taskPermissions";
import { TEST_IDS } from "../../constants/testIds";
import { useAuthStore } from "../../store/authStore";
import { useTasksStore } from "../../store/tasksStore";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import {
  utcToLocalInput,
  localInputToUtc,
  formatInTz,
  todayLocalInputDefault,
} from "../../lib/dates";
import { Trash2, Plus, Send, Repeat, GripVertical, Maximize2, X, Lock } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import AttachmentsSection from "./AttachmentsSection";
import ProjectFolderFiles from "./ProjectFolderFiles";
import CommentReactions from "./CommentReactions";
import MentionTextarea from "./MentionTextarea";
import useTenantWebSocket from "../../hooks/useTenantWebSocket";

function newSubtask(title = "", dueAt = null) {
  return {
    id: Math.random().toString(36).slice(2, 10),
    title,
    done: false,
    due_at: dueAt,
  };
}

/**
 * Slack-inspired right-side detail panel. Full peer of TaskDialog:
 * details/subtasks/comments/files/activity, WS-driven typing indicator,
 * mention textarea and comment reactions.
 *
 * onExpand — invoked when user clicks the Expand icon: parent should
 * close the panel and open TaskDialog for the same task.
 */
export default function TaskRightPanel({
  task,
  users,
  lists = [],
  onClose,
  onSave,
  onDelete,
  onExpand,
  tenantTz,
  initialTab = "details",
}) {
  const { t } = useTranslation();
  const { user: me } = useAuthStore();
  const { updateSubtasks } = useTasksStore();
  const [form, setForm] = useState({
    title: "",
    description: "",
    status: "todo",
    priority: "medium",
    assignee_id: null,
    co_assignees: [],
    list_id: null,
    visibility: "everyone",
    visible_to_ids: [],
    due_at: "",
    recurrence: "none",
  });
  const [subtasks, setSubtasks] = useState([]);
  const [newSub, setNewSub] = useState("");
  const [newSubDue, setNewSubDue] = useState(() => todayLocalInputDefault(tenantTz));
  const [comments, setComments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [tab, setTab] = useState("details");
  const [typingUsers, setTypingUsers] = useState({});
  const [confirmReassign, setConfirmReassign] = useState(null); // { targetId, targetName }
  const typingTimerRef = useRef(null);

  // Row-level RBAC — same check the backend uses in PATCH /tasks/{id}.
  // Includes ultra_admin in the admin bypass.
  const canEditProtected = canEditProtectedTaskFields(me, task);
  const canReassign = canEditProtected;

  useEffect(() => {
    if (task) {
      setForm({
        title: task.title || "",
        description: task.description || "",
        status: task.status,
        priority: task.priority,
        assignee_id: task.assignee_id,
        co_assignees: (task.assignee_ids || []).filter(
          (id) => id && id !== task.assignee_id,
        ),
        list_id: task.list_id || null,
        visibility: task.visibility || "everyone",
        visible_to_ids: task.visible_to_ids || [],
        due_at: task.due_at ? utcToLocalInput(task.due_at, tenantTz || "UTC") : "",
        recurrence: task.recurrence || "none",
      });
      setSubtasks(task.subtasks || []);
      setNewSub("");
      setNewSubDue(todayLocalInputDefault(tenantTz));
      setTab(initialTab || "details");
      loadComments();
      loadActivity();
      api.post("/users/me/mark-read", { task_id: task.id }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, initialTab]);

  useTenantWebSocket((msg) => {
    if (!msg?.event || !task) return;
    if (msg.task_id && msg.task_id !== task.id) return;
    if (msg.event === "reaction.updated") {
      setComments((cs) =>
        cs.map((c) =>
          c.id === msg.comment_id ? { ...c, reactions: msg.reactions } : c,
        ),
      );
    } else if (msg.event === "comment.created") {
      loadComments();
      loadActivity();
    } else if (msg.event === "typing") {
      if (!msg.user_id || msg.user_id === me?.id) return;
      setTypingUsers((cur) => {
        const next = { ...cur };
        if (msg.is_typing) next[msg.user_id] = Date.now();
        else delete next[msg.user_id];
        return next;
      });
    }
  });

  useEffect(() => {
    const int = setInterval(() => {
      const now = Date.now();
      setTypingUsers((cur) => {
        let changed = false;
        const next = {};
        for (const [k, ts] of Object.entries(cur)) {
          if (now - ts < 5000) next[k] = ts;
          else changed = true;
        }
        return changed ? next : cur;
      });
    }, 2000);
    return () => clearInterval(int);
  }, []);

  const emitTyping = (is_typing) => {
    const ws = window.__taskoraWs;
    if (!ws || ws.readyState !== 1 || !task) return;
    try {
      ws.send(JSON.stringify({ event: "typing", task_id: task.id, is_typing }));
    } catch {
      /* ignore */
    }
  };

  const handleCommentChange = (val) => {
    setNewComment(val);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    if (val.length > 0) {
      emitTyping(true);
      typingTimerRef.current = setTimeout(() => emitTyping(false), 2500);
    } else {
      emitTyping(false);
    }
  };

  const loadComments = async () => {
    if (!task) return;
    const { data } = await api.get(`/tasks/${task.id}/comments`);
    setComments(data);
  };
  const loadActivity = async () => {
    if (!task) return;
    const { data } = await api.get(`/tasks/${task.id}/activity`);
    setActivity(data);
  };

  const userMap = Object.fromEntries(users.map((u) => [u.id, u]));
  const projectLists = (lists || []).filter(
    (l) => !task?.project_id || l.project_id === task.project_id,
  );

  const persist = async () => {
    const patch = {
      title: form.title,
      description: form.description,
      status: form.status,
      priority: form.priority,
      due_at: form.due_at ? localInputToUtc(form.due_at, tenantTz || "UTC") : null,
      recurrence: form.recurrence,
    };
    // Only include assignee fields in the patch if the caller is allowed to
    // change them AND they actually changed — otherwise the backend would 403.
    const oldCo = (task.assignee_ids || []).filter(
      (id) => id && id !== task.assignee_id,
    );
    const coChanged =
      JSON.stringify(oldCo) !== JSON.stringify(form.co_assignees || []);
    if (canReassign && (form.assignee_id !== task.assignee_id || coChanged)) {
      // Tam liste gönder — backend birincili ilk elemandan türetir.
      patch.assignee_ids = [
        ...(form.assignee_id ? [form.assignee_id] : []),
        ...(form.co_assignees || []).filter((id) => id !== form.assignee_id),
      ];
    }
    // Grup (proje listesi) değişimi — herkes taşıyabilir (guarded değil).
    if (form.list_id && form.list_id !== task.list_id) {
      patch.list_id = form.list_id;
    }
    // Görünürlük — yalnızca görevi oluşturan (veya admin) değiştirebilir.
    const canChangeVisibility =
      !!me &&
      (me.id === task.created_by ||
        ["super_admin", "ultra_admin"].includes(me.role));
    const visChanged =
      (form.visibility || "everyone") !== (task.visibility || "everyone") ||
      JSON.stringify(form.visible_to_ids || []) !==
        JSON.stringify(task.visible_to_ids || []);
    if (canChangeVisibility && visChanged) {
      patch.visibility = form.visibility || "everyone";
      patch.visible_to_ids =
        form.visibility === "custom" ? form.visible_to_ids || [] : [];
    }
    try {
      await onSave(patch);
    } catch (err) {
      // safeUpdateTask upstream already toasted; abort save.
      return false;
    }
    try {
      await updateSubtasks(task.id, subtasks);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      return false;
    }
    toast.success(t("common.saved"));
    onClose();
    return true;
  };

  const submit = async (e) => {
    e?.preventDefault();
    // If the reassign would transfer ownership away from the current user
    // (and they are not super_admin/creator), warn first.
    if (
      canReassign &&
      form.assignee_id !== task.assignee_id &&
      me?.id === task.assignee_id &&
      !["super_admin", "ultra_admin"].includes(me?.role) &&
      me?.id !== task.created_by &&
      form.assignee_id &&
      form.assignee_id !== me.id
    ) {
      const target = users.find((u) => u.id === form.assignee_id);
      setConfirmReassign({
        targetId: form.assignee_id,
        targetName: target?.name || "",
      });
      return;
    }
    await persist();
  };

  const addSubtask = () => {
    if (!newSub.trim()) return;
    setSubtasks((s) => [
      ...s,
      newSubtask(
        newSub.trim(),
        newSubDue ? localInputToUtc(newSubDue, tenantTz || "UTC") : null,
      ),
    ]);
    setNewSub("");
    setNewSubDue(todayLocalInputDefault(tenantTz));
  };
  const toggleSub = (id) => {
    setSubtasks((s) => s.map((x) => (x.id === id ? { ...x, done: !x.done } : x)));
  };
  const removeSub = (id) => {
    setSubtasks((s) => s.filter((x) => x.id !== id));
  };
  const setSubDue = (id, localVal) => {
    const iso = localVal ? localInputToUtc(localVal, tenantTz || "UTC") : null;
    setSubtasks((s) => s.map((x) => (x.id === id ? { ...x, due_at: iso } : x)));
  };

  const postComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      await api.post(`/tasks/${task.id}/comments`, { body: newComment.trim() });
      setNewComment("");
      loadComments();
      loadActivity();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteComment = async (cid) => {
    try {
      await api.delete(`/tasks/${task.id}/comments/${cid}`);
      loadComments();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const subtaskProgress = subtasks.length
    ? Math.round((subtasks.filter((s) => s.done).length / subtasks.length) * 100)
    : 0;

  return (
    <Sheet open={!!task} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl md:max-w-2xl p-0 flex flex-col overflow-hidden [&>button.absolute]:hidden"
        data-testid={TEST_IDS.taskPanel}
      >
        <SheetHeader className="border-b border-zinc-100 px-6 py-4 relative">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-500 mb-1">
                {t("tasks.title")}
              </div>
              <SheetTitle className="font-display text-xl truncate">
                {task?.title || "Task"}
              </SheetTitle>
              <SheetDescription className="sr-only">
                Task detail panel — edit fields, subtasks and comments.
              </SheetDescription>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={onExpand}
                data-testid={TEST_IDS.taskPanelExpand}
                title={t("tasks.expand")}
                className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
              >
                <Maximize2 className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={onClose}
                data-testid="task-right-panel-x"
                title={t("common.close")}
                className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </SheetHeader>

        <Tabs
          value={tab}
          onValueChange={setTab}
          className="flex-1 overflow-hidden flex flex-col"
        >
          <div className="border-b border-zinc-100 px-6 pt-3">
            <TabsList className="bg-zinc-100">
              <TabsTrigger value="details" data-testid={TEST_IDS.taskPanelTabDetails}>
                Details
              </TabsTrigger>
              <TabsTrigger value="subtasks" data-testid={TEST_IDS.taskPanelTabSubtasks}>
                Subtasks ({subtasks.length})
              </TabsTrigger>
              <TabsTrigger value="comments" data-testid={TEST_IDS.taskPanelTabComments}>
                Comments ({comments.length})
              </TabsTrigger>
              <TabsTrigger value="files" data-testid={TEST_IDS.taskPanelTabFiles}>
                Files
              </TabsTrigger>
              <TabsTrigger value="activity" data-testid={TEST_IDS.taskPanelTabActivity}>
                Activity
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            <TabsContent value="details" className="mt-0 space-y-4">
              <form id="task-panel-form" onSubmit={submit} className="space-y-4">
                {!canEditProtected && (
                  <ReassignRequestNotice task={task} />
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">
                    {t("tasks.title")}
                  </Label>
                  <Input
                    required
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    data-testid={TEST_IDS.taskPanelTitle}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">
                    {t("tasks.description")}
                  </Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1"
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                      {t("tasks.status")}
                      {!canEditProtected && (
                        <span title="Sana atanmamış görev — sadece görüntüleniyor">🔒</span>
                      )}
                    </Label>
                    <Select
                      value={form.status}
                      disabled={!canEditProtected}
                      onValueChange={(v) => setForm({ ...form, status: v })}
                    >
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todo">{t("tasks.status_todo")}</SelectItem>
                        <SelectItem value="in_progress">{t("tasks.status_in_progress")}</SelectItem>
                        <SelectItem value="done">{t("tasks.status_done")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">
                      {t("tasks.priority")}
                    </Label>
                    <Select
                      value={form.priority}
                      onValueChange={(v) => setForm({ ...form, priority: v })}
                    >
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["low", "medium", "high", "urgent"].map((p) => (
                          <SelectItem key={p} value={p}>{t(`tasks.priority_${p}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                      {t("tasks.assignee")}
                      {!canReassign && (
                        <span title={t("tasks.reassign_locked_hint")}>🔒</span>
                      )}
                    </Label>
                    <Select
                      value={form.assignee_id || "__none__"}
                      disabled={!canReassign}
                      onValueChange={(v) =>
                        setForm({ ...form, assignee_id: v === "__none__" ? null : v })
                      }
                    >
                      <SelectTrigger
                        className="mt-1"
                        data-testid="panel-assignee-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t("tasks.unassigned")}</SelectItem>
                        {users
                          .filter((u) => u.active !== false)
                          .map((u) => (
                            <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {!canReassign && (
                      <p className="mt-1 text-[11px] text-zinc-500">
                        {t("tasks.reassign_locked_hint")}
                      </p>
                    )}
                    {/* Ortak sorumlular — görev hepsinin ekranında görünür,
                        biri tamamlayınca herkes için tamamlanır. */}
                    {(form.co_assignees.length > 0 || canReassign) && (
                      <div className="mt-2">
                        {form.co_assignees.length > 0 && (
                          <div className="mb-1 flex flex-wrap items-center gap-1">
                            {form.co_assignees.map((id) => {
                              const u = userMap[id];
                              return (
                                <span
                                  key={id}
                                  className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700"
                                  data-testid={`co-assignee-chip-${id}`}
                                >
                                  {u?.name || "?"}
                                  {canReassign && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setForm({
                                          ...form,
                                          co_assignees: form.co_assignees.filter(
                                            (x) => x !== id,
                                          ),
                                        })
                                      }
                                      data-testid={`co-assignee-remove-${id}`}
                                      className="text-violet-400 hover:text-red-600"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  )}
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {canReassign && (
                          <Select
                            value=""
                            onValueChange={(v) => {
                              if (
                                v &&
                                !form.co_assignees.includes(v) &&
                                v !== form.assignee_id
                              )
                                setForm({
                                  ...form,
                                  co_assignees: [...form.co_assignees, v],
                                });
                            }}
                          >
                            <SelectTrigger
                              className="h-7 text-[11px] text-zinc-500"
                              data-testid="panel-co-assignee-add"
                            >
                              <SelectValue placeholder="+ Ortak sorumlu ekle" />
                            </SelectTrigger>
                            <SelectContent>
                              {users
                                .filter(
                                  (u) =>
                                    u.active !== false &&
                                    u.id !== form.assignee_id &&
                                    !form.co_assignees.includes(u.id),
                                )
                                .map((u) => (
                                  <SelectItem key={u.id} value={u.id}>
                                    {u.name}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">
                      {t("tasks.due_at")} ({tenantTz || "UTC"})
                    </Label>
                    <Input
                      type="datetime-local"
                      value={form.due_at}
                      disabled={!canEditProtected}
                      onChange={(e) => setForm({ ...form, due_at: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
                {projectLists.length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">
                      Grup
                    </Label>
                    <Select
                      value={form.list_id || ""}
                      onValueChange={(v) => setForm({ ...form, list_id: v })}
                    >
                      <SelectTrigger
                        className="mt-1"
                        data-testid="panel-list-select"
                      >
                        <SelectValue placeholder="Grup seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {projectLists.map((l) => (
                          <SelectItem key={l.id} value={l.id}>
                            {l.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {/* Görünürlük — gizli görevler diğer kullanıcılara görünmez */}
                <div>
                  <Label className="flex items-center gap-1 text-xs uppercase tracking-wider text-zinc-500">
                    <Lock className="h-3 w-3" />
                    Görünürlük
                  </Label>
                  {(() => {
                    const canChangeVisibility =
                      !!me &&
                      (me.id === task.created_by ||
                        ["super_admin", "ultra_admin"].includes(me.role));
                    return (
                      <>
                        <Select
                          value={form.visibility || "everyone"}
                          onValueChange={(v) =>
                            setForm({ ...form, visibility: v })
                          }
                          disabled={!canChangeVisibility}
                        >
                          <SelectTrigger
                            className="mt-1"
                            data-testid="panel-visibility-select"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="everyone">Herkes</SelectItem>
                            <SelectItem value="private">Sadece Ben</SelectItem>
                            <SelectItem value="participants">
                              Sadece Atanmışlar (oluşturan + sorumlular)
                            </SelectItem>
                            <SelectItem value="custom">
                              Seçili kişiler
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        {!canChangeVisibility && (
                          <p className="mt-1 text-[11px] text-zinc-500">
                            Görünürlüğü yalnızca görevi oluşturan kişi
                            değiştirebilir.
                          </p>
                        )}
                        {form.visibility === "custom" && (
                          <div className="mt-2">
                            {(form.visible_to_ids || []).length > 0 && (
                              <div className="mb-1 flex flex-wrap items-center gap-1">
                                {form.visible_to_ids.map((id) => {
                                  const u = userMap[id];
                                  return (
                                    <span
                                      key={id}
                                      className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700"
                                      data-testid={`visibility-chip-${id}`}
                                    >
                                      {u?.name || "?"}
                                      {canChangeVisibility && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setForm({
                                              ...form,
                                              visible_to_ids:
                                                form.visible_to_ids.filter(
                                                  (x) => x !== id,
                                                ),
                                            })
                                          }
                                          data-testid={`visibility-remove-${id}`}
                                          className="text-amber-400 hover:text-red-600"
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      )}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                            {canChangeVisibility && (
                              <Select
                                value=""
                                onValueChange={(v) => {
                                  if (
                                    v &&
                                    !(form.visible_to_ids || []).includes(v)
                                  )
                                    setForm({
                                      ...form,
                                      visible_to_ids: [
                                        ...(form.visible_to_ids || []),
                                        v,
                                      ],
                                    });
                                }}
                              >
                                <SelectTrigger
                                  className="h-7 text-[11px] text-zinc-500"
                                  data-testid="panel-visibility-add"
                                >
                                  <SelectValue placeholder="+ Görebilecek kişi ekle" />
                                </SelectTrigger>
                                <SelectContent>
                                  {users
                                    .filter(
                                      (u) =>
                                        u.active !== false &&
                                        u.id !== me?.id &&
                                        !(form.visible_to_ids || []).includes(
                                          u.id,
                                        ),
                                    )
                                    .map((u) => (
                                      <SelectItem key={u.id} value={u.id}>
                                        {u.name}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                    <Repeat className="h-3 w-3" />
                    {t("tasks.recurrence")}
                  </Label>
                  <Select
                    value={form.recurrence}
                    onValueChange={(v) => setForm({ ...form, recurrence: v })}
                  >
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t("tasks.recurrence_none")}</SelectItem>
                      <SelectItem value="daily">{t("tasks.recurrence_daily")}</SelectItem>
                      <SelectItem value="weekly">{t("tasks.recurrence_weekly")}</SelectItem>
                      <SelectItem value="monthly">{t("tasks.recurrence_monthly")}</SelectItem>
                      <SelectItem value="yearly">{t("tasks.recurrence_yearly")}</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.recurrence !== "none" && !form.due_at && (
                    <p className="mt-1 text-xs text-amber-700">
                      {t("tasks.recurrence_needs_due")}
                    </p>
                  )}
                </div>
              </form>
            </TabsContent>

            <TabsContent value="subtasks" className="mt-0 space-y-3">
              {subtasks.length > 0 && (
                <div className="mb-4">
                  <div className="mb-1 flex items-center justify-between text-xs text-zinc-500">
                    <span>{subtaskProgress}% complete</span>
                    <span>{subtasks.filter((s) => s.done).length}/{subtasks.length}</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
                    <div
                      className="h-full bg-zinc-900 transition-all duration-300"
                      style={{ width: `${subtaskProgress}%` }}
                    />
                  </div>
                </div>
              )}
              <ul className="space-y-2">
                {subtasks.map((s, idx) => (
                  <li
                    key={s.id}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/plain", String(idx));
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
                      if (Number.isNaN(from) || from === idx) return;
                      setSubtasks((cur) => {
                        const next = [...cur];
                        const [moved] = next.splice(from, 1);
                        next.splice(idx, 0, moved);
                        return next;
                      });
                    }}
                    className="group flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 cursor-move"
                  >
                    <GripVertical className="h-3 w-3 text-zinc-300" />
                    <Checkbox
                      checked={s.done}
                      onCheckedChange={() => toggleSub(s.id)}
                    />
                    <span
                      className={[
                        "flex-1 text-sm",
                        s.done ? "text-zinc-400 line-through" : "text-zinc-900",
                      ].join(" ")}
                    >
                      {s.title}
                    </span>
                    <Input
                      type="datetime-local"
                      value={s.due_at ? utcToLocalInput(s.due_at, tenantTz || "UTC") : ""}
                      onChange={(e) => setSubDue(s.id, e.target.value)}
                      data-testid={`subtask-due-${s.id}`}
                      className="w-[170px] h-7 text-[11px] px-2 opacity-70 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                      title={t("tasks.due_at") + " (" + (tenantTz || "UTC") + ")"}
                    />
                    <button
                      type="button"
                      onClick={() => removeSub(s.id)}
                      className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
                {subtasks.length === 0 && (
                  <li className="rounded-md border border-dashed border-zinc-200 px-3 py-6 text-center text-xs text-zinc-500">
                    {t("tasks.no_subtasks")}
                  </li>
                )}
              </ul>
              <div className="flex items-center gap-2">
                <Input
                  placeholder={t("tasks.add_subtask")}
                  value={newSub}
                  onChange={(e) => setNewSub(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addSubtask();
                    }
                  }}
                  className="flex-1"
                  data-testid="subtask-title-input"
                />
                <Input
                  type="datetime-local"
                  value={newSubDue}
                  onChange={(e) => setNewSubDue(e.target.value)}
                  className="w-[170px] h-9 text-xs"
                  data-testid="subtask-due-input"
                  title={t("tasks.due_at") + " (" + (tenantTz || "UTC") + ")"}
                />
                <Button
                  type="button"
                  onClick={addSubtask}
                  size="sm"
                  variant="outline"
                  className="gap-1 border-zinc-300"
                  data-testid="subtask-add-btn"
                >
                  <Plus className="h-3 w-3" />
                  {t("common.add")}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="comments" className="mt-0 space-y-3">
              <ul className="space-y-3 max-h-[calc(100vh-320px)] overflow-y-auto pr-1">
                {comments.map((c) => (
                  <li key={c.id} className="rounded-lg border border-zinc-200 bg-white p-3">
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="font-medium text-zinc-900">
                        {userMap[c.user_id]?.name || "—"}
                      </span>
                      <span className="text-zinc-500">
                        {formatInTz(c.created_at, tenantTz, "DD MMM HH:mm")}
                      </span>
                    </div>
                    <div className="prose prose-sm prose-zinc max-w-none whitespace-pre-wrap text-sm text-zinc-700 [&_a]:text-blue-600 [&_code]:rounded [&_code]:bg-zinc-100 [&_code]:px-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_u]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-zinc-300 [&_blockquote]:pl-3 [&_blockquote]:text-zinc-600">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                      >
                        {c.body}
                      </ReactMarkdown>
                    </div>
                    <CommentReactions
                      comment={c}
                      users={users}
                      onUpdated={(reactions) =>
                        setComments((cs) =>
                          cs.map((x) => (x.id === c.id ? { ...x, reactions } : x)),
                        )
                      }
                    />
                    {c.user_id === me?.id && (
                      <button
                        type="button"
                        onClick={() => deleteComment(c.id)}
                        className="mt-1 text-[10px] uppercase tracking-wider text-zinc-400 hover:text-red-600"
                      >
                        Delete
                      </button>
                    )}
                  </li>
                ))}
                {comments.length === 0 && (
                  <li className="rounded-md border border-dashed border-zinc-200 px-3 py-8 text-center text-xs text-zinc-500">
                    {t("tasks.no_comments")}
                  </li>
                )}
              </ul>
              <form onSubmit={postComment} className="space-y-1">
                {Object.keys(typingUsers).length > 0 && (
                  <div className="text-[11px] italic text-zinc-500">
                    {Object.keys(typingUsers)
                      .map((id) => userMap[id]?.name || "Someone")
                      .join(", ")}{" "}
                    {t("tasks.typing")}
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <MentionTextarea
                    value={newComment}
                    onChange={handleCommentChange}
                    users={users}
                    placeholder={t("tasks.add_comment")}
                    rows={2}
                    className="flex-1"
                    showToolbar
                    data-testid="comment-composer-textarea"
                  />
                  <Button
                    type="submit"
                    size="sm"
                    className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
                  >
                    <Send className="h-3 w-3" />
                    {t("common.send")}
                  </Button>
                </div>
                <p className="text-[10px] text-zinc-500">{t("tasks.markdown_hint")}</p>
              </form>
            </TabsContent>

            <TabsContent value="files" className="mt-0">
              <AttachmentsSection taskId={task?.id} tenantTz={tenantTz} />
              <ProjectFolderFiles projectId={task?.project_id} />
            </TabsContent>

            <TabsContent value="activity" className="mt-0">
              <ul className="space-y-3 max-h-[calc(100vh-260px)] overflow-y-auto pr-1">
                {activity.map((a, i) => {
                  const isReassign = a.action === "reassigned";
                  const fromU = isReassign && userMap[a.meta?.from_user_id];
                  const toU = isReassign && userMap[a.meta?.to_user_id];
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <div className="mt-1 h-1.5 w-1.5 rounded-full bg-zinc-400 shrink-0" />
                      <div className="flex-1 text-sm">
                        <span className="font-medium text-zinc-900">
                          {userMap[a.user_id]?.name || "system"}
                        </span>{" "}
                        {isReassign ? (
                          <span className="text-zinc-500">
                            {t("tasks.activity_reassigned", {
                              from: fromU?.name || t("tasks.unassigned"),
                              to: toU?.name || t("tasks.unassigned"),
                            })}
                          </span>
                        ) : (
                          <>
                            <span className="text-zinc-500">{a.action}</span>
                            {a.changes && (
                              <span className="text-zinc-500"> · {a.changes.join(", ")}</span>
                            )}
                          </>
                        )}
                        <div className="text-xs text-zinc-400">
                          {formatInTz(a.timestamp, tenantTz, "DD MMM HH:mm:ss")}
                        </div>
                      </div>
                    </li>
                  );
                })}
                {activity.length === 0 && (
                  <li className="rounded-md border border-dashed border-zinc-200 px-3 py-8 text-center text-xs text-zinc-500">
                    {t("tasks.no_activity")}
                  </li>
                )}
              </ul>
            </TabsContent>
          </div>
        </Tabs>

        <div className="border-t border-zinc-100 px-6 py-3 flex items-center justify-between bg-zinc-50/60">
          <Button
            type="button"
            variant="ghost"
            onClick={onDelete}
            data-testid={TEST_IDS.taskPanelDelete}
            className="gap-1 text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
            {t("tasks.delete")}
          </Button>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid={TEST_IDS.taskPanelClose}
            >
              {t("common.close")}
            </Button>
            <Button
              type="button"
              onClick={submit}
              data-testid={TEST_IDS.taskPanelSave}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
            >
              {t("tasks.save")}
            </Button>
          </div>
        </div>

        <AlertDialog
          open={!!confirmReassign}
          onOpenChange={(v) => !v && setConfirmReassign(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("tasks.reassign_confirm_title")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("tasks.reassign_confirm_body", {
                  name: confirmReassign?.targetName || "",
                  task: task?.title || "",
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="panel-reassign-cancel-btn">
                {t("common.no")}
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="panel-reassign-confirm-btn"
                onClick={async () => {
                  setConfirmReassign(null);
                  await persist();
                }}
                className="bg-zinc-900 text-white hover:bg-zinc-800"
              >
                {t("common.yes")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}

/** Amber read-only banner with an inline "→ Görevi bana ata iste" CTA.
 *  Renders inside TaskRightPanel when the caller lacks row-level edit
 *  perms. Fires `POST /tasks/{id}/request-assignment` — server rate
 *  limits to 1 per 24h per (user, task). */
function ReassignRequestNotice({ task }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const request = async () => {
    if (busy || done) return;
    setBusy(true);
    try {
      await api.post(`/tasks/${task.id}/request-assignment`);
      setDone(true);
      toast.success(
        "Talebin gönderildi — göreve atanan kişi ve oluşturana bildirim iletildi.",
      );
    } catch (err) {
      if (err.response?.status === 429) {
        setDone(true);
        toast.info(
          formatApiErrorDetail(err.response?.data?.detail) ||
            "Son 24 saat içinde zaten talep gönderildi.",
        );
      } else {
        toast.error(
          formatApiErrorDetail(err.response?.data?.detail) ||
            "Talep gönderilemedi",
        );
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      data-testid="task-panel-readonly-notice"
      className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-[11px] leading-relaxed text-amber-900"
    >
      <div className="flex items-start gap-2">
        <span className="mt-0.5 text-sm" aria-hidden>🔒</span>
        <div>
          <b>Sadece görüntüleme</b> — Bu görev sana atanmamış ve senin
          oluşturduğun bir görev değil. Durum, atanan kişi ve son tarih
          değiştirilemez.
        </div>
      </div>
      <button
        type="button"
        onClick={request}
        disabled={busy || done}
        data-testid="task-panel-request-assignment"
        className="self-start inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-1 text-[11px] font-medium text-amber-900 shadow-sm transition-colors hover:border-amber-400 hover:bg-amber-100 disabled:opacity-60"
      >
        {busy
          ? "Gönderiliyor..."
          : done
            ? "Talep gönderildi ✓"
            : "→ Bu görevi bana ata iste"}
      </button>
    </div>
  );
}
